import React, { useEffect, useState } from "react";
import { Empty, Select, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { _fetch_bonus_account_history } from "../common/axios_services";

const ReferralHistory = ({
  referralPool,
  incomeHistory,
  incomeHistoryMeta,
  incomeLoading,
  loading,
  incomeTypeFilter,
  setIncomeTypeFilter,
  INCOME_TYPES,
  page,
  setPage,
  referralCount = 0,
  bonusBalance = 0,
}) => {
  const [activeTab, setActiveTab] = useState("income");
  const [referralSubTab, setReferralSubTab] = useState("referrals");
  const [bonusHistory, setBonusHistory] = useState([]);
  const [bonusLoading, setBonusLoading] = useState(false);
  const antIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

  useEffect(() => {
    const fetchBonusHistory = async () => {
      setBonusLoading(true);
      try {
        const response = await _fetch_bonus_account_history({ page: 1, page_size: 50 });
        const payload = response?.data?.data ?? response?.data ?? {};
        const records = Array.isArray(payload)
          ? payload
          : payload?.bonuses || payload?.data || [];
        setBonusHistory(Array.isArray(records) ? records : []);
      } catch (error) {
        setBonusHistory([]);
      } finally {
        setBonusLoading(false);
      }
    };

    fetchBonusHistory();
  }, []);

  const totalBonusHistoryAmount = bonusHistory.reduce(
    (sum, item) => sum + Math.abs(Number(item.amount || 0)),
    0
  );
  const displayBonusTotal = bonusHistory.length > 0 ? totalBonusHistoryAmount : Number(bonusBalance || 0);

  const referralData =
    referralPool?.data || (Array.isArray(referralPool) ? referralPool : []);

  return (
    <div className="mt history-data-detail">
      <div className="top-history-header">
        <div
          onClick={() => setActiveTab("income")}
          className={`history-box cursor-pointer ${
            activeTab === "income" ? "active-history" : ""
          }`}
        >
          <h3 className="tile-header">Income History</h3>
        </div>
        <div
          onClick={() => setActiveTab("referrals")}
          className={`history-box cursor-pointer ${
            activeTab === "referrals" ? "active-history" : ""
          }`}
        >
          <h3 className="tile-header">Referrals ({referralCount})</h3>
        </div>
      </div>

      {activeTab === "income" && (
        <div className="contai">
          <div className="mb-3 mt-2">
            <Select
              placeholder="Filter by type"
              value={incomeTypeFilter}
              onChange={(val) => {
                setIncomeTypeFilter(val);
                setPage(1);
              }}
              style={{ width: "100%" }}
              size="large"
            >
              {INCOME_TYPES.map((t) => (
                <Select.Option key={t.key} value={t.key}>
                  {t.label}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Spin spinning={incomeLoading} indicator={antIcon}>
            {incomeHistory.length > 0 ? (
              <div className="history-list">
                {incomeHistory.map((item, index) => (
                  <IncomeItem key={index} history={item} />
                ))}
                {incomeHistoryMeta?.hasMore && (
                  <div className="text-center mt-3 mb-3">
                    <button
                      onClick={() => setPage(page + 1)}
                      className="text-blue-600 font-bold text-sm underline"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Empty description="No income history found" />
            )}
          </Spin>
        </div>
      )}

      {activeTab === "referrals" && (
        <div className="contai">
          <div className="mb-3 mt-2">
            <Select
              value={referralSubTab}
              onChange={setReferralSubTab}
              style={{ width: "100%" }}
              size="large"
              options={[
                { value: "referrals", label: `Referrals (${referralCount})` },
                { value: "bonus", label: `Bonus History (₦${Number(displayBonusTotal || 0).toLocaleString()})` },
              ]}
            />
          </div>

          {referralSubTab === "referrals" && (
            loading ? (
              <div className="empty-data">
                <div>Loading...</div>
              </div>
            ) : referralData.length > 0 ? (
              <div className="history-list">
                {referralData.map((item, index) => (
                  <ReferralItem key={index} history={item} />
                ))}
              </div>
            ) : (
              <Empty description="No referrals found" />
            )
          )}

          {referralSubTab === "bonus" && (
            <Spin spinning={bonusLoading} indicator={antIcon}>
              {bonusHistory.length > 0 ? (
                <div className="history-list">
                  {bonusHistory.map((item, index) => <BonusItem key={item.id || index} history={item} />)}
                </div>
              ) : <Empty description="No bonus history found" />}
            </Spin>
          )}
        </div>
      )}
    </div>
  );
};

const IncomeItem = ({ history }) => {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(history?.dateEarned || "").toLocaleDateString("en-US", {
    dateStyle: "short",
  });
  const amount = Number(history?.incomeEarned || 0).toFixed(2);

  return (
    <div className="history-item-container">
      <div
        className="history-item-summary clickable"
        onClick={() => setExpanded(!expanded)}
      >
        <span>
          {history?.service || "Income"} on {date}
        </span>
        <span>{expanded ? " ▲" : " ▼"}</span>
      </div>
      {expanded && (
        <div className="expanded-details">
          <DetailRow label="Date Earned" value={date} />
          <DetailRow label="Service" value={history?.service || "N/A"} />
          <DetailRow label="Order Ref" value={history?.orderRef || "N/A"} />
          <DetailRow label="Income Earned" value={`NGN ${amount}`} isNumeric />
        </div>
      )}
    </div>
  );
};

const ReferralItem = ({ history }) => {
  const [expanded, setExpanded] = useState(false);
  const formatTier = (tier) =>
    !tier ? "N/A" : tier.replace("_", " ").toUpperCase();
  const fullName = `${history?.fullName || ""}`.trim() || "Unknown User";
  const date = new Date(
    history?.dateJoined?.split("T")[0] || "",
  ).toLocaleDateString("en-US", { dateStyle: "full" });

  return (
    <div className="history-item-container">
      <div
        className="history-item-summary clickable"
        onClick={() => setExpanded(!expanded)}
      >
        <span>
          {fullName} {history?.isAmbassador && "👑"} on {date}
        </span>
        <span>{expanded ? " ▲" : " ▼"}</span>
      </div>
      {expanded && (
        <div className="expanded-details">
          <DetailRow label="Full Name" value={fullName} />
          <DetailRow label="Date Joined" value={date} />
          <DetailRow label="Tier" value={formatTier(history?.tier)} />
          <DetailRow
            label="Referral Commission"
            value={history?.referralCommission}
          />
          <DetailRow label="Status" value={history?.status} />
          {history?.isAmbassador && (
            <DetailRow label="Role" value="Konnect Ambassador" />
          )}
        </div>
      )}
    </div>
  );
};

const BonusItem = ({ history }) => {
  const [expanded, setExpanded] = useState(false);
  const dateValue = history?.timestamp || history?.createdAt;
  const date = dateValue ? new Date(dateValue).toLocaleDateString("en-US", { dateStyle: "full" }) : "N/A";
  const amount = Number(history?.amount ?? history?.total_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const type = history?.payment_type || history?.type || history?.service || "Bonus";

  return (
    <div className="history-item-container">
      <div className="history-item-summary clickable" onClick={() => setExpanded(!expanded)}>
        <span>NGN {amount} {type} on {date}</span>
        <span>{expanded ? " ▲" : " ▼"}</span>
      </div>
      {expanded && (
        <div className="expanded-details">
          <DetailRow label="Date" value={date} />
          <DetailRow label="Type" value={type} />
          <DetailRow label="Amount" value={`NGN ${amount}`} isNumeric />
          <DetailRow label="Description" value={history?.description || history?.service || "N/A"} />
          <DetailRow label="Status" value={history?.status || "Completed"} />
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, isNumeric }) => (
  <div className="detail-row">
    <div className="detail-label">{label}:</div>
    <div className={`detail-value ${isNumeric ? "numeric" : ""}`}>{value}</div>
  </div>
);

export default ReferralHistory;
